import type { CatalogueOf } from '@croissants/i18n';
import type { GuestEnCatalogue } from './en';

/**
 * French catalogue for `order.`. French is fully supported at launch and every
 * shipped locale is complete at all times (PRD NFR-006), which the
 * `satisfies CatalogueOf<GuestEnCatalogue>` below turns into a compile error the
 * moment a key is added to English and forgotten here.
 *
 * Voice: French identity, franglais tone (PRD WEB-024) - warm and short, never a
 * literal translation of the English.
 */
export const fr = {
  // Shell and navigation
  'app.skipToContent': 'Aller au contenu',
  /* The shell's own controls (packages/ui `AppShell`). They are accessible names
     only - the glyphs carry no text - and each pair changes with the state it
     describes, so a screen reader hears what the control will DO next. */
  'app.openMenu': 'Ouvrir le menu',
  'app.closeMenu': 'Fermer le menu',
  'app.collapseSidebar': 'Réduire le menu latéral',
  'app.expandSidebar': 'Déployer le menu latéral',
  'app.loading': 'Chargement',
  'app.mainLabel': 'Contenu principal',
  'nav.menu': 'Menu',
  'nav.basket': 'Panier',
  'nav.orders': 'Mes commandes',
  'nav.label': 'Sections',
  'nav.basketCount': {
    one: 'Panier, {count} article',
    other: 'Panier, {count} articles',
  },

  // Language switch (GST-006)
  'language.label': 'Langue',
  'language.en': 'English',
  'language.fr': 'Français',
  'language.switchToEn': 'Switch to English',
  'language.switchToFr': 'Passer en français',

  'support.title': 'Nous contacter',
  'support.whatsapp': 'WhatsApp',
  'support.call': 'Appeler le {number}',
  'support.mail.writeAbove': 'Merci d’écrire votre message au-dessus de cette ligne.',
  'support.mail.apartment': 'Appartement',
  'support.mail.address': 'Adresse',
  'support.mail.orders': 'Commandes',
  'support.mail.language': 'Langue',
  'support.mail.device': 'Appareil',
  'support.mail.subject': 'Assistance Croissant in Bed',
  'support.mail.subjectForApartment': 'Assistance Croissant in Bed - {apartment}',

  // QR landing (GST-001, GST-028)
  'landing.resolving': 'Ouverture de la boutique de votre appartement',
  'landing.noToken.title': 'Scannez le code de votre appartement',
  'landing.noToken.body':
    'Cette boutique s’ouvre depuis le QR code laissé par votre hôte dans l’appartement. Scannez-le avec l’appareil photo de votre téléphone.',
  'landing.invalid.title': 'Ce code n’a pas fonctionné',
  'landing.invalid.body':
    'Il a peut-être été remplacé. Demandez le code actuel à votre hôte, ou contactez-nous.',
  'landing.invalid.contactAction': 'Nous contacter',
  'landing.unreachable.title': 'Nous n’avons pas pu ouvrir la boutique',
  'landing.deliveringTo': 'Livraison à {place}',

  // Storefront (GST-003, GST-029)
  'menu.photoPosition': 'Photo {position} sur {count}',
  'menu.empty': 'Rien sur le comptoir pour ce matin, pour l’instant.',
  'menu.openLabel': 'Voir {name}',
  'menu.addQuantity': 'Ajouter {count} au panier \u00b7 {amount}',
  'menu.addLabel': 'Ajouter {name}',
  'menu.inBasket': {
    one: '{count} au panier',
    other: '{count} au panier',
  },
  'menu.dietary': 'Convient aux régimes',
  'menu.maxPerOrder': 'Jusqu’à {count} par commande',
  'menu.formule.included': 'Inclus',
  'menu.formule.required': 'Obligatoire',
  'menu.viewBasket': 'Voir le panier',
  'menu.setDeliveryTime': 'Choisir l’heure',
  'menu.scheduleDelivery': 'Planifier cette livraison',
  'menu.arrivingToday': 'Aujourd’hui, au plus tôt',
  'menu.allergen.GLUTEN': 'Gluten',
  'menu.allergen.CRUSTACEANS': 'Crustacés',
  'menu.allergen.EGGS': 'Œufs',
  'menu.allergen.FISH': 'Poisson',
  'menu.allergen.PEANUTS': 'Arachides',
  'menu.allergen.SOYBEANS': 'Soja',
  'menu.allergen.MILK': 'Lait',
  'menu.allergen.NUTS': 'Fruits à coque',
  'menu.allergen.CELERY': 'Céleri',
  'menu.allergen.MUSTARD': 'Moutarde',
  'menu.allergen.SESAME': 'Sésame',
  'menu.allergen.SULPHITES': 'Sulfites',
  'menu.allergen.LUPIN': 'Lupin',
  'menu.allergen.MOLLUSCS': 'Mollusques',
  'menu.crossContamination': 'Peut contenir des traces des 14 allergènes majeurs.',
  'menu.dietary.VEGETARIAN': 'Végétarien',
  'menu.dietary.VEGAN': 'Vegan',
  'menu.dietary.GLUTEN_FREE': 'Sans gluten',
  'menu.dietary.LACTOSE_FREE': 'Sans lactose',
  'menu.dietary.HALAL': 'Halal',

  // Shop chooser, mode B (GST-030 to GST-032)
  'shops.title': 'Choisissez votre boulangerie',
  'shops.subtitle': 'Ces maisons peuvent livrer votre adresse.',
  'shops.empty': 'Aucune boulangerie ne dessert encore cette adresse ce matin-là.',
  'shops.select': 'Voir le comptoir',
  'shops.everyDay': 'Tous les jours',
  'shops.closedOn': 'fermé {days}',
  'shops.deliveryToday': "Livraison aujourd'hui {times}",
  'shops.deliveryTomorrow': 'Livraison demain {times}',
  'shops.deliveryLater': 'Livraison {day} {times}',
  'shops.deliveryUntil': "jusqu'à {time}",
  'shops.closedUntil': 'Boulangerie fermée, réouverture {date}',
  'shops.change': 'Changer de boulangerie',
  'shops.changeConfirmTitle': 'Changer de boulangerie ?',
  'shops.changeConfirmAction': 'Changer et vider le panier',

  // Mode B2 : la fenêtre de livraison et la ligne sous l'adresse (GST-045)
  'delivery.title': 'Choisissez votre livraison',
  'delivery.close': 'Fermer',
  'delivery.now': 'Commander maintenant',
  'delivery.later': 'Planifier pour plus tard',
  'delivery.in': 'Livraison dans',
  'delivery.inMinutes': 'Livraison dans environ {minutes} min',
  'delivery.lineInMinutes': 'Dans environ {minutes} min',
  'delivery.bakerOpen': 'Votre boulanger est ouvert',
  'delivery.closed': 'Votre boulanger est actuellement fermé',
  'delivery.opensToday': "Ouvre aujourd'hui à {time}",
  'delivery.opensTomorrow': 'Ouvre demain à {time}',
  'delivery.opensLater': 'Ouvre {day} à {time}',
  'delivery.chosen': '{day}, {window}',

  // Basket (GST-026, GST-032)
  'basket.title': 'Votre panier',
  'basket.empty': 'Votre panier est vide',
  'basket.emptyBody': 'Ajoutez une formule ou un extra : il apparaîtra ici.',
  'basket.browse': 'Voir le comptoir',
  'basket.increase': 'Un {name} de plus',
  'basket.decrease': 'Un {name} de moins',
  'basket.remove': 'Retirer {name}',
  'basket.quantity': 'Quantité de {name}',
  'basket.subtotal': 'Articles',
  'basket.continue': 'Récapitulatif et paiement',
  'basket.fulfilledBy': 'Chez {shop}',
  'basket.lineUnavailable': 'Pas servi ce matin-là',
  'basket.removeShort': 'Retirer',
  'basket.unavailableTitle': 'Certains articles ne sont pas servis ce matin-là',
  'basket.unavailableBody':
    'La carte change d’un matin à l’autre, et ceux-ci n’y sont pas. Retirez-les et choisissez autre chose : le reste de votre panier ne bouge pas.',
  'basket.removeUnavailable': 'Retirer ces articles',
  'basket.orderNote': 'Note de commande',
  'basket.orderNoteAdd': 'Ajouter une note à la commande',
  'basket.orderNotePlaceholder': 'Ex. : merci de couper la baguette, pas de serviettes',

  // Window picker (GST-002) - premiere etape en mode A, apres la carte de la
  // maison en mode B (GST-030)
  'windows.selected': 'Livraison {window}',
  'windows.none': 'Aucun créneau n’est ouvert pour ce matin.',
  'windows.windowLabel': 'Créneaux de livraison disponibles pour les jours choisis',
  'windows.pickADay': 'Choisissez un matin ci-dessus pour voir ses créneaux de livraison.',
  'windows.noneForSelection':
    'Aucun créneau ne convient à tous les matins choisis. Désélectionnez-en un pour en voir davantage.',
  'windows.today': 'Aujourd’hui',
  'windows.tomorrow': 'Demain',
  'windows.orderForDays': {
    one: 'Commande pour 1 jour',
    other: 'Commande pour {count} jours',
  },
  'windows.confirm': 'Planifier la livraison',
  'windows.confirmNow': 'Livrer maintenant',
  'windows.soonest': 'Au plus tôt',
  'windows.blocked.unavailable': 'Indisponible',
  'windows.nowIs': 'il est {time}',
  'windows.change': 'Modifier',
  'windows.orderFor': 'Commande pour',
  'windows.conflictTitle': 'Ce créneau ne convient plus',
  'windows.conflictBody':
    'Votre panier demande plus de préparation que le créneau choisi ne le permet : il ne peut plus arriver à cette heure-là. Voici les créneaux les plus proches qui fonctionnent.',
  'windows.alternative': '{day}, {window}',
  'windows.conflictAction': 'Voir tous les créneaux',

  // Checkout (GST-006, GST-021 to GST-026)
  'checkout.step.details': 'Vos coordonnées',
  'checkout.step.review': 'Récapitulatif',
  'checkout.step.pay': 'Paiement',
  'checkout.stepOf': 'Étape {step} sur {total}',
  'checkout.whatsappUpdates': 'Suivi sur WhatsApp',
  'checkout.required': '(obligatoire)',
  'checkout.optional': '(facultatif)',
  'checkout.items': 'Votre commande',
  'checkout.itemUnnamed': 'Article (nom indisponible)',
  'checkout.windowFrom': 'à partir de {time}',
  'checkout.editItems': 'Modifier les articles',
  'checkout.itemsUnavailable':
    'Votre commande contient encore des articles qui ne sont pas servis le matin choisi : impossible de la chiffrer. Modifiez vos articles pour continuer.',
  'checkout.editDetails': 'Modifier vos coordonnées',
  'checkout.correctDetails': 'Corrigez ou complétez ce dont le livreur a besoin',
  'checkout.correctDetailsHint':
    'Modifiez ou ajoutez des instructions supplémentaires pour le livreur',
  'checkout.door.LEFT': 'Gauche',
  'checkout.door.RIGHT': 'Droite',
  'checkout.door.CENTRE': 'Centre',
  'checkout.door.FACING': 'Face à l’escalier',
  'checkout.legal.terms': 'Conditions générales',
  'checkout.legal.howItWorks': 'Fonctionnement',
  'checkout.legal.privacy': 'Politique de confidentialité',
  'checkout.legal.legal': 'Mentions légales',
  'checkout.legal.intro': 'Avant de payer',
  'footer.legalLabel': 'Informations légales',
  'checkout.stayTotal': 'Total de votre séjour',
  'checkout.stayMornings': {
    one: '{count} matin',
    other: '{count} matins',
  },
  'checkout.morningUnavailable': 'Indisponible',
  'checkout.stayReason.BAKERY_CLOSED': 'Boulangerie fermée',
  'checkout.stayReason.NO_CAPACITY': 'Complet',
  'checkout.stayReason.NO_BAKERY_AVAILABLE': 'Aucune boulangerie ne peut livrer',
  'checkout.stayReason.WRITE_FAILED': 'Impossible à enregistrer',
  'checkout.goToPayment': 'Aller au paiement',
  'checkout.validation.name': 'Indiquez un nom.',
  'checkout.validation.phone': 'Mobile avec indicatif.',
  'checkout.validation.email': 'Cette adresse e-mail semble incorrecte.',
  'checkout.validation.window': 'Merci de choisir un matin et un créneau de livraison.',
  'checkout.validation.basket': 'Votre panier est vide.',

  // Payment (GST-005, PAY-002)
  'checkout.itemCount': {
    one: '{count} article',
    other: '{count} articles',
  },
  'checkout.addressAndInstructions': 'Adresse et instructions',
  'checkout.brief.building': 'bâtiment {name}',
  'checkout.brief.floor': 'étage {label}',
  'checkout.brief.groundFloor': 'rez-de-chaussée',
  'checkout.brief.doorPosition': 'porte {position}',
  'checkout.brief.doorNumber': 'porte {number}',
  'checkout.brief.intercom': 'interphone {name}',
  'checkout.brief.lift': 'ascenseur',
  'checkout.brief.noLift': 'sans ascenseur',
  'checkout.scheduledFor': 'Planifié pour',
  'checkout.scheduledDays': { one: '1 jour', other: '{count} jours' },
  'checkout.estimatedArrival': 'Arrivée estimée',
  'checkout.instructionsAdd': 'Ajouter des instructions',
  'checkout.accessCodeSet': 'Code enregistré',
  'checkout.addressAdd': 'Ajouter étage, porte, interphone',
  'payment.orByCard': 'ou payer par carte',
  'payment.walletNoContact':
    'Ce portefeuille ne nous a pas donné de nom ni de numéro pour le livreur. Merci de payer par carte.',
  'payment.title': 'Paiement',
  'payment.summary': 'Vous payez {amount}',
  'payment.wallets': 'Apple Pay, Google Pay et les cartes sont acceptés.',
  'payment.neverStored':
    'Les données de votre carte vont directement à notre prestataire de paiement. Nous ne les voyons jamais.',
  'payment.confirm': 'Payer {amount}',
  'payment.processing': 'Confirmation de votre paiement',
  'payment.failed': 'Le paiement n’a pas abouti.',
  'payment.testMode.title': 'Mode test',
  'payment.testMode.body':
    'Aucune clé de prestataire de paiement n’est configurée ici : la commande est autorisée avec le moyen de paiement de test de la plateforme. Aucune carte n’est débitée.',
  'payment.testMode.action': 'Passer la commande de test',
  'payment.unavailable': 'Le formulaire de paiement n’a pas pu se charger. Réessayez.',

  // Confirmation
  'confirm.title': 'Votre petit-déjeuner est lancé',
  'confirm.body': 'Commande {code}. Cette page se met à jour toute seule.',
  'confirm.track': 'Suivre cette commande',
  'confirm.scheduleCreated': 'Nous avons aussi planifié les autres matins de votre séjour.',
  // Une ligne par matin que la commande n'a pas pu couvrir (F-24).
  'confirm.skipped.BAKERY_CLOSED': '{day} : boulangerie fermée',
  'confirm.skipped.NO_CAPACITY': '{day} : plus de créneau disponible à cette heure',
  'confirm.skipped.NO_BAKERY_AVAILABLE': '{day} : aucune boulangerie ne peut livrer',
  'confirm.skipped.WRITE_FAILED': '{day} : n’a pas pu être passée',

  // Order detail (GST-008, GST-035, GST-039)
  'order.courierTitle': 'Livraison',
  'order.line.insteadOf': 'à la place de {name}',
  'order.courierUnnamed': 'Votre livreur',
  'order.courierLocation': 'Position en direct',
  'order.courierLocationAt': 'Mise à jour {time}',
  'order.trackWithProvider': 'Ouvrir le suivi en direct',
  'order.map.label': 'Où se trouve votre livreur',
  'order.map.shop': 'Point de retrait',
  'order.map.home': 'Votre adresse',
  'order.map.courier': 'Livreur',
  'courier.vehicleType.WALKER': 'À pied',
  'courier.vehicleType.BICYCLE': 'Vélo',
  'courier.vehicleType.SCOOTER': 'Scooter',
  'courier.vehicleType.MOTORCYCLE': 'Moto',
  'courier.vehicleType.CAR': 'Voiture',
  'courier.vehicleType.VAN': 'Camionnette',
  'courier.vehicleType.TRUCK': 'Camion',
  'order.deliveryDetails': 'Adresse de livraison',
  'order.placedAt': 'Passée {time}',
  'order.summaryTitle': 'Votre commande',
  'order.reviewSubmitted': 'Tout ce que vous avez envoyé',
  'order.notFound': 'Commande introuvable pour ce code.',
  'order.error.accessBody': 'Le lien est peut-être ancien. Consultez vos commandes.',
  'order.error.linkTitle': 'Lien incomplet',
  'order.error.linkBody': 'Consultez plutôt vos commandes.',
  'order.error.unreachableTitle': 'Problème de connexion',
  'order.backToOrders': 'Retour à vos commandes',
  'order.backToStorefront': 'Retour à la boutique',
  'order.refundNote': 'Remboursement de {amount}.',
  'order.reportIssue': 'Signaler un problème',
  'order.requestInvoice': 'Demander une facture',
  'order.invoiceRequested':
    'Votre facture sera bientôt disponible ici et vous sera envoyée par e-mail',
  'order.invoiceRequestedOk': 'OK',
  'order.ending.ok': 'OK',
  'order.invoice': 'Facture',
  'order.productsInvoicePending': 'Facture des produits en préparation',
  'order.invoiceTag.creditNote': 'Avoir',
  'order.invoiceTag.canceled': 'Annulée',
  'order.substitutionChoice': 'Remplacement si un article manque',
  'order.soldOut.title': 'Épuisé à votre boulangerie',
  'order.soldOut.refund': 'La différence sera remboursée.',
  'order.deliveredAt': 'Livrée {time}',
  'order.unfulfilled.action': 'Commander pour demain',

  // Tip and ratings (GST-037, GST-038)
  'tip.amountLabel': 'Montant du pourboire',
  'tip.customLabel': 'En euros',
  'tip.unavailable': 'Le pourboire n’est pas disponible pour cette livraison.',
  'rating.starLabel': '{count} sur 5',
  'rating.deliveryHelp': 'Livreur, ponctualité, remise.',

  // Issue reporting (GST-039)
  'issue.photoLabel': 'Ajouter une photo',
  'issue.photoCamera': 'Appareil photo',
  'issue.photoLibrary': 'Photothèque',
  'issue.photoPreparing': 'Préparation...',
  'issue.photoAlt': 'Votre photo',
  'issue.photoRemove': 'Retirer',
  'issue.photoNotImage': 'Ce n’est pas une image.',
  'issue.photoTooLarge': 'Photo trop lourde.',
  'issue.photoUnreadable': 'Photo illisible.',
  'issue.photoUploadFailed': 'Impossible de joindre cette photo.',
  'issue.noText': 'Dites-nous ce qui s’est passé.',
  'issue.email': 'Votre e-mail',
  'issue.emailHint': 'Pour vous répondre. Nous répondons par e-mail, pas par SMS.',
  'issue.noEmail': 'Indiquez un e-mail pour que nous puissions vous répondre.',
  'issue.invalidEmail': 'Cet e-mail ne semble pas valide.',
  'issue.wholeOrder': 'Toute la commande',

  // Receipt (PAY-022)
  'receipt.tip': 'Pourboire livreur',

  // Not-servable and paused (GST-040)
  'unavailable.contact':
    'Nous ouvrons de nouveaux quartiers en permanence. Votre hôte peut nous interroger sur celui-ci.',
  'unavailable.contactAction': 'Nous contacter',
  'unavailable.rescan': 'Scanner un autre code',
  'storefront.closed.title': 'Les commandes sont fermées pour le moment',
  'unavailable.backHome': 'Retour',

  // Errors and generic UI
  'error.title': 'Un souci est survenu',
  'error.requestId': 'Référence : {id}',
  'error.sessionExpired': 'Cette session de boutique a expiré. Rescannez le code pour la rouvrir.',
  'error.rescan': 'Rouvrir la boutique',
  // Repeat schedules (GST-010, section 4.3)

  'ui.yes': 'Oui',
  'ui.no': 'Non',
  'ui.masked': 'Masqué',
  'ui.none': 'Non renseigné',
  'ui.dismiss': 'Fermer',
} as const satisfies CatalogueOf<GuestEnCatalogue>;
